import { WO_STATUS } from "./woUtils";
import { stc } from "@/lib/orderStatus";
import { AlertTriangle, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { STATUS, STATUS_KEYS, MONTHS, WEEKDAYS, iso, todayIso, monthGrid, daysSince, daysText, waLink, itDate } from "./utils";

export function LateAlert({ late, pending, cfg, onMark }) {
  if (!late.length) return null;
  const summary = [`Ordini da fare (${late.length} fermi da oltre ${cfg.threshold_days} giorni):`, ...late.map((o) => `• ${o.name} — ${o.item} (${daysText(daysSince(o.date))})`)].join("\n");
  return (
    <section className="rounded-xl border-2 border-rose-300 bg-white overflow-hidden" data-testid="counter-late-alert">
      <div className="p-4 flex flex-wrap items-center justify-between gap-3 bg-rose-50">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-rose-600 shrink-0" />
          <div>
            <h2 className="text-lg font-extrabold uppercase text-rose-800" data-testid="counter-late-count">{late.length} {late.length === 1 ? "ordine da fare" : "ordini da fare"}</h2>
            <p className="text-xs text-rose-900/80">Fermi in sospeso da più di {cfg.threshold_days} giorni · in tutto {pending} da ordinare al fornitore.</p>
          </div>
        </div>
        <a href={waLink("", summary)} target="_blank" rel="noreferrer" data-testid="counter-late-wa-btn" className="press h-9 px-3 rounded-md bg-[#25D366] text-white text-xs font-semibold flex items-center gap-1.5">
          <MessageCircle className="w-4 h-4" /> Mandami la lista su WhatsApp
        </a>
      </div>
      <div className="divide-y">
        {late.map((o) => (
          <div key={o.id} className="px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`counter-late-${o.id}`}>
            <span className="font-mono text-[11px] font-semibold px-2 py-0.5 rounded bg-rose-100 text-rose-800">{daysText(daysSince(o.date))}</span>
            <b>{o.name}</b><span className="text-slate-600 flex-1 min-w-[140px]">{o.item}</span>
            <button data-testid={`counter-late-mark-${o.id}`} onClick={() => onMark(o, "lavorazione")} className="press h-8 px-3 rounded-md text-white text-xs font-semibold" style={{ backgroundColor: stc("lavorazione").fill }}>Segna ordinato</button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function StatusCards({ counts, filter, setFilter, doneToday, showDone, setShowDone }) {
  const card = (k, n, title, sub, on, onClick) => (
    <button key={k} data-testid={`counter-filter-${k}`} onClick={onClick} style={{ backgroundColor: stc(k).fill }}
      className={`press text-left rounded-xl p-4 text-white transition-shadow ${on ? "ring-4 ring-offset-2 ring-brand-navy shadow-lg" : "hover:shadow-md"}`}>
      <div className="text-3xl sm:text-4xl font-extrabold" data-testid={`counter-count-${k}`}>{n}</div>
      <div className="text-base font-bold">{title}</div>
      <div className="hidden sm:block text-sm text-white/90">{sub}</div>
    </button>
  );
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="counter-status-cards">
      {STATUS_KEYS.filter((k) => k !== "completato").map((k) => card(k, counts[k] || 0, STATUS[k].label, STATUS[k].sub, filter === k, () => setFilter(filter === k ? "" : k)))}
      {card("completato", doneToday, "Ritirati oggi", showDone ? "completati visibili in elenco" : "tocca per mostrare i completati", showDone, () => setShowDone(!showDone))}
    </div>
  );
}

export function MonthNav({ cur, setCur, setDay }) {
  const go = (d) => setCur(new Date(cur.getFullYear(), cur.getMonth() + d, 1));
  return (
    <div className="flex items-center gap-2">
      <button data-testid="counter-prev-month" onClick={() => go(-1)} className="press h-9 w-9 grid place-items-center rounded-md border bg-white"><ChevronLeft className="w-4 h-4" /></button>
      <h2 className="text-xl font-extrabold uppercase w-44 text-center" data-testid="counter-month-label">{MONTHS[cur.getMonth()]} {cur.getFullYear()}</h2>
      <button data-testid="counter-next-month" onClick={() => go(1)} className="press h-9 w-9 grid place-items-center rounded-md border bg-white"><ChevronRight className="w-4 h-4" /></button>
      <button data-testid="counter-today-btn" onClick={() => { const t = new Date(); setCur(new Date(t.getFullYear(), t.getMonth(), 1)); setDay(todayIso()); }} className="press h-9 px-3 rounded-md border bg-white text-sm font-semibold">Oggi</button>
    </div>
  );
}

const chip = (o) => (o.kind === "wo" ? `${WO_STATUS[o.status].chip} ${o.priority === "urgente" ? "ring-1 ring-rose-600 font-bold" : ""}` : STATUS[o.status].chip);
const dot = (o) => (o.kind === "wo" ? (o.priority === "urgente" ? "bg-rose-600" : WO_STATUS[o.status].dot) : STATUS[o.status].dot);

export function MonthCalendar({ cur, byDay, day, setDay, onOpen }) {
  const today = todayIso();
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid="counter-calendar">
      <div className="grid grid-cols-7 bg-brand-navy text-white">{WEEKDAYS.map((d) => <div key={d} className="py-2 text-center font-mono text-[11px] uppercase">{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {monthGrid(cur.getFullYear(), cur.getMonth()).map((d) => {
          const k = iso(d);
          const list = byDay[k] || [];
          const out = d.getMonth() !== cur.getMonth();
          return (
            <button key={k} data-testid={`counter-day-cell-${k}`} onClick={() => setDay(k)}
              className={`min-h-[64px] sm:min-h-[92px] p-1.5 border-t border-l text-left align-top transition-colors ${out ? "bg-slate-50 text-slate-400" : "bg-white hover:bg-brand-yellow/10"} ${day === k ? "ring-2 ring-inset ring-brand-yellow bg-brand-yellow/10" : ""}`}>
              <span className={`inline-grid place-items-center w-6 h-6 rounded-full text-xs font-bold ${k === today ? "bg-brand-yellow text-brand-navy" : ""}`}>{d.getDate()}</span>
              <div className="hidden sm:block space-y-0.5 mt-0.5">
                {list.slice(0, 3).map((o) => (
                  <div key={o.id} role="button" data-testid={`cal-event-${o.id}`} onClick={(e) => { if (o.kind === "wo" && onOpen) { e.stopPropagation(); setDay(k); onOpen(o); } }}
                    className={`truncate text-[11px] px-1 rounded ${chip(o)}`}>{o.kind === "wo" ? `${o.priority === "urgente" ? "! " : ""}${o.client.name} · ${o.product_type}` : o.name}</div>
                ))}
                {list.length > 3 && <div className="text-[10px] text-slate-500">+{list.length - 3} altri</div>}
              </div>
              {list.length > 0 && <div className="sm:hidden flex gap-0.5 mt-1">{list.slice(0, 4).map((o) => <span key={o.id} className={`w-1.5 h-1.5 rounded-full ${dot(o)}`} />)}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const dayTitle = (k) => (k ? `${itDate(k)}` : "");
